import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOAuth } from '../hooks/useOAuth';
import configService from '../services/configService';
import bookingService from '../services/bookingService';
import { useToast } from './Toast';

const INTEGRATIONS = [
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Let your AI assistant check availability and book appointments directly into your Google Calendar.',
    category: 'Calendar',
    iconSvg: (
      <svg viewBox="0 0 48 48" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="6" width="36" height="36" rx="4" fill="#fff" stroke="#E5E7EB" strokeWidth="1"/>
        <rect x="6" y="14" width="36" height="6" fill="#4285F4"/>
        <rect x="6" y="20" width="36" height="22" rx="0" fill="#fff"/>
        <rect x="6" y="20" width="36" height="2" fill="#4285F4"/>
        <text x="24" y="36" textAnchor="middle" fontSize="14" fontWeight="700" fill="#4285F4" fontFamily="sans-serif">31</text>
        <rect x="16" y="4" width="3" height="8" rx="1.5" fill="#5F6368"/>
        <rect x="29" y="4" width="3" height="8" rx="1.5" fill="#5F6368"/>
      </svg>
    ),
    accentColor: '#4285F4',
  },
  {
    id: 'calendly',
    name: 'Calendly',
    description: 'Connect Calendly to route booking requests through your existing Calendly scheduling pages.',
    category: 'Calendar',
    iconSvg: (
      <svg viewBox="0 0 48 48" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="20" fill="#006BFF" opacity="0.12"/>
        <text x="24" y="30" textAnchor="middle" fontSize="20" fontWeight="700" fill="#006BFF" fontFamily="sans-serif">C</text>
      </svg>
    ),
    accentColor: '#006BFF',
    comingSoon: true,
  },
];

const StatusBadge = ({ status }) => {
  if (status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
        Connected
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block"></span>
        Reconnect required
      </span>
    );
  }
  return null;
};

const StaffCalendarStatus = ({ staff, availability }) => {
  const avail = availability.find(a => a.staff_id === staff.staff_id);
  const hasCalendar = !!avail?.google_calendar_id;
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-700">{staff.name}</span>
      {hasCalendar ? (
        <span className="inline-flex items-center gap-1 text-xs text-green-600">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
          Calendar syncing
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
          Pending setup
        </span>
      )}
    </div>
  );
};

function Integrations() {
  const { user } = useAuth();
  const { connectService } = useOAuth();
  const { showToast } = useToast();
  const [connections, setConnections] = useState({});
  const [assistantPhone, setAssistantPhone] = useState(null);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [staff, setStaff] = useState([]);
  const [staffAvailability, setStaffAvailability] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);

  const loadConnections = useCallback(async () => {
    try {
      const data = await configService.getOAuthConnections();
      const map = {};
      (data.connections || []).forEach(c => {
        map[c.service] = c;
      });
      setConnections(map);
      return map;
    } catch (err) {
      console.error('Failed to load connections:', err);
      return {};
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  const loadStaffCalendarStatus = useCallback(async () => {
    setLoadingStaff(true);
    try {
      const [staffData, availData] = await Promise.all([
        bookingService.listStaff(),
        bookingService.listStaffAvailability(),
      ]);
      setStaff(staffData || []);
      setStaffAvailability(availData || []);
    } catch (err) {
      console.error('Failed to load staff calendar status:', err);
    } finally {
      setLoadingStaff(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const map = await loadConnections();
      if (map['google_calendar']?.status === 'connected') {
        loadStaffCalendarStatus();
      }

      try {
        const cognitoUserId = user?.attributes?.sub || user?.username;
        if (!cognitoUserId) return;
        const config = await configService.getBusinessConfigByUserId(cognitoUserId);
        if (config?.assistant_phone) setAssistantPhone(config.assistant_phone);
      } catch (err) {
        console.error('Failed to load assistant phone:', err);
      }
    };
    init();
  }, [loadConnections, loadStaffCalendarStatus, user]);

  const handleDisconnect = async (serviceId) => {
    setDisconnecting(serviceId);
    try {
      await configService.disconnectOAuthService(serviceId);
      showToast('Integration disconnected.');
      setStaff([]);
      setStaffAvailability([]);
      await loadConnections();
    } catch (err) {
      showToast(err.message || 'Failed to disconnect', 'error');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnect = async (serviceId) => {
    setConnecting(serviceId);
    try {
      await connectService(serviceId, assistantPhone);
      showToast('Calendar connected successfully!');
      await loadConnections();
      if (serviceId === 'google_calendar') {
        await loadStaffCalendarStatus();
      }
    } catch (err) {
      if (err.message !== 'OAuth process timed out') {
        showToast(err.message || 'Failed to connect', 'error');
      }
    } finally {
      setConnecting(null);
    }
  };

  const getStatus = (serviceId) => {
    const conn = connections[serviceId];
    if (!conn) return 'disconnected';
    return conn.status || 'connected';
  };

  if (loadingConnections) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="mb-6">
        <div className="text-4xl font-bold text-gray-900 mb-2">Integrations</div>
        <p className="text-sm text-gray-600">
          Connect your calendar and scheduling tools so your AI assistant can manage bookings automatically.
        </p>
      </div>

      {/* Calendar section */}
      <div>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Calendar</h2>
        <div className="space-y-3">
          {INTEGRATIONS.filter(i => i.category === 'Calendar').map((integration) => {
            const status = getStatus(integration.id);
            const isConnecting = connecting === integration.id;
            const isConnected = status === 'connected';
            const isExpired = status === 'expired';
            const showStaffStatus = integration.id === 'google_calendar' && isConnected;

            return (
              <div
                key={integration.id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col gap-4"
              >
                {/* Icon + Info + Action row */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: integration.accentColor + '12' }}
                    >
                      {integration.iconSvg}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-gray-900">{integration.name}</span>
                        <StatusBadge status={status} />
                        {integration.comingSoon && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            Coming soon
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 leading-snug">{integration.description}</p>
                    </div>
                  </div>

                  {/* Action button */}
                  {!integration.comingSoon && (
                    <div className="flex items-center gap-2 sm:flex-shrink-0 justify-end">
                      {isConnected ? (
                        <>
                          <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-500">
                            <i className="fa fa-check text-green-500 text-xs"></i>
                            Connected
                          </span>
                          <button
                            onClick={() => handleDisconnect(integration.id)}
                            disabled={disconnecting === integration.id}
                            className="px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {disconnecting === integration.id ? (
                              <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin inline-block"></span>
                            ) : (
                              'Disconnect'
                            )}
                          </button>
                        </>
                      ) : isExpired ? (
                        <button
                          onClick={() => handleConnect(integration.id)}
                          disabled={isConnecting}
                          className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isConnecting ? (
                            <>
                              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              Connecting...
                            </>
                          ) : (
                            'Reconnect'
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnect(integration.id)}
                          disabled={isConnecting}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isConnecting ? (
                            <>
                              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              Connecting...
                            </>
                          ) : (
                            'Connect'
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Per-staff calendar status — shown only when Google Calendar is connected */}
                {showStaffStatus && (
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Staff Calendars
                    </p>
                    {loadingStaff ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <span className="w-3 h-3 border-2 border-gray-300 border-t-blue-400 rounded-full animate-spin inline-block"></span>
                        Loading staff...
                      </div>
                    ) : staff.length === 0 ? (
                      <p className="text-sm text-gray-400">
                        No staff added yet.{' '}
                        <a href="/staff" className="text-blue-500 hover:underline">Add staff</a>
                        {' '}to enable per-staff calendar sync.
                      </p>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {staff.map(s => (
                          <StaffCalendarStatus
                            key={s.staff_id}
                            staff={s}
                            availability={staffAvailability}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Info callout */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 flex gap-3">
        <i className="fa fa-info-circle text-blue-500 mt-0.5 flex-shrink-0"></i>
        <div>
          <p className="text-sm font-medium text-blue-900 mb-0.5">How it works</p>
          <p className="text-sm text-blue-700 leading-relaxed">
            When a caller asks to book an appointment, your AI assistant checks your connected calendar for
            availability and creates the booking automatically. A dedicated calendar is created for each staff
            member — external bookings sync automatically so your assistant always sees accurate availability.
            Your credentials are encrypted and stored securely.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Integrations;
